import { ApexOptions } from 'apexcharts';

import { WeekdayWeekHeatmapSeries } from '../../utils/to-weekday-week-heatmap-series';
import { formatAxisLabel, formatAxisWeekday } from '../shared/format-axis-label';
import { getThemeColorHex } from '../shared/resolve-css-color-to-hex';

export interface WeekdayWeekHeatmapChartOptionsConfig {
    height: number;
    width?: number;
    maxValue: number;
    weekLabels: string[];
    tooltipSeries: WeekdayWeekHeatmapSeries[];
}

export const getWeekdayWeekHeatmapChartOptions = (config: WeekdayWeekHeatmapChartOptionsConfig): ApexOptions => {
    const { height, width, maxValue, weekLabels, tooltipSeries } = config;
    const primaryColor = getThemeColorHex('--color-brand-primary', '#1C3A4A');
    const emptyColor = getThemeColorHex('--color-border-default', '#e5e7eb');

    const colorRanges =
        maxValue > 0
            ? [
                  {
                      from: 0,
                      to: 0,
                      color: emptyColor,
                      name: 'none'
                  },
                  {
                      from: 1,
                      to: maxValue,
                      color: primaryColor,
                      name: 'volume'
                  }
              ]
            : [
                  {
                      from: 0,
                      to: 0,
                      color: emptyColor,
                      name: 'none'
                  }
              ];

    return {
        chart: {
            height,
            width: width || '100%',
            type: 'heatmap',
            parentHeightOffset: 0,
            toolbar: {
                show: false
            }
        },
        colors: [primaryColor],
        fill: {
            opacity: 1
        },
        dataLabels: {
            enabled: false
        },
        legend: {
            show: false
        },
        grid: {
            padding: {
                right: 8,
                left: 4
            }
        },
        xaxis: {
            type: 'category',
            categories: weekLabels,
            tooltip: {
                enabled: false
            },
            labels: {
                rotate: 0,
                hideOverlappingLabels: true,
                style: {
                    cssClass: 'apexcharts-xaxis-label fill-foreground-default'
                },
                formatter: (value) => formatAxisLabel(String(value))
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: {
            labels: {
                showDuplicates: true,
                style: {
                    colors: ['var(--color-foreground-default)']
                }
            }
        },
        plotOptions: {
            heatmap: {
                shadeIntensity: 0.8,
                radius: 2,
                enableShades: maxValue > 0,
                colorScale: {
                    ranges: colorRanges
                }
            }
        },
        tooltip: {
            custom: ({ seriesIndex, dataPointIndex }) => {
                const point = tooltipSeries[seriesIndex]?.data?.[dataPointIndex];
                if (!point) {
                    return '';
                }

                // Date is always the calendar day for this grid cell (week Monday + weekday offset).
                const cellDate = point.meta.date;
                const dateLabel = formatAxisLabel(cellDate);
                const dayLabel = formatAxisWeekday(cellDate) || tooltipSeries[seriesIndex]?.name || '';

                return `<div class="bg-background-surface outline-border-default rounded-md">
                    <div class="text-foreground-default border-b border-b-border-default p-2 bg-background-default/50 w-full text-sm font-semibold">${dateLabel}</div>
                    <div class="text-foreground-muted p-2 text-xs">${dayLabel}</div>
                    <div class="text-foreground-muted p-2 text-xs">${point.y}</div>
                </div>`;
            }
        }
    };
};
